// UnsavedBar.tsx · v12.10.11.1 (#1477 docked flush to the bottom edge, full content width, spacer so the last field scrolls clear) · 2026-10-09 · VoiceSpectrum AI
// UnsavedBar.tsx · v12.10.10 (#1405 the card's TITLE only - never its ? help text; the bar stays one line) · 2026-10-08 · VoiceSpectrum AI
// UnsavedBar.tsx · v12.10.9.4 (#1335 F7 - sticky unsaved-changes bar, UI-PROTO-T40 "F7" LOCKED 10/7) · 2026-10-07 · VoiceSpectrum AI
// "● N unsaved changes · <card names> · Discard · Save now" - pinned to the bottom of every Settings page while anything on it
// is unsaved (Tushar 10/7: "i come into impression that the changes are auto saved"). It reads the cards themselves: every
// Settings card is a .settings-section-card that carries .dirty while it has unsaved changes (Organization, AI Agent, Call
// Handling, Reliability, Knowledge Base, Communication, Personalization, Vocabulary, Integrations, Security › Platform incl.
// Support access, User groups). One change = one card with unsaved edits. A card name scrolls to its card.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface DirtyCard { id: string; title: string }

// v12.10.10 (#1405): the heading carries the "?" help popover INSIDE it, so textContent returned
// "Lists?What it doesHolds the legal values..." - the bar printed the whole help text and grew to cover the page.
// Read the heading's own words: a clone with every help / tooltip / badge / button element removed.
export function headingTitle(h: Element | null): string {
  if (!h) return '';
  const c = h.cloneNode(true) as Element;
  c.querySelectorAll('[role="tooltip"], .tip, .help, .help-tip, .tenant-help-scope, .ai-agent-panel, .settings-help, .hint, button, svg, .settings-card-dirty, .badge, .pill')
    .forEach((n) => n.remove());
  const t = (c.textContent ?? '').replace(/Unsaved$/, '').replace(/\s+/g, ' ').trim();
  return t.length > 60 ? `${t.slice(0, 57)}...` : t;
}

/** The unsaved cards on the page, kept live while the page changes. */
export function useDirtyCards(rootSelector: string, active: boolean): DirtyCard[] {
  const [cards, setCards] = useState<DirtyCard[]>([]);
  useEffect(() => {
    if (!active) { setCards([]); return; }
    const read = () => {
      const root = document.querySelector(rootSelector);
      if (!root) { setCards([]); return; }
      const list = Array.from(root.querySelectorAll<HTMLElement>('.settings-section-card.dirty, .ug-dirty-card')).map((el, i) => {
        if (!el.id) el.id = `unsaved-card-${i}`;
        const t = el.getAttribute('data-card-title') || headingTitle(el.querySelector('h2, h3')) || 'Changes';
        return { id: el.id, title: t };
      });
      setCards((prev) => (prev.length === list.length && prev.every((p, i) => p.id === list[i].id && p.title === list[i].title) ? prev : list));
    };
    read();
    const root = document.querySelector(rootSelector) ?? document.body;
    const mo = new MutationObserver(read);
    mo.observe(root, { subtree: true, attributes: true, attributeFilter: ['class'], childList: true });
    return () => mo.disconnect();
  }, [rootSelector, active]);
  return cards;
}

export const changesText = (n: number) => `${n} unsaved ${n === 1 ? 'change' : 'changes'}`;

export default function UnsavedBar({ cards, fallbackTitle, saving, onSave, onDiscard }: {
  cards: DirtyCard[]; fallbackTitle: string; saving: boolean; onSave: () => void; onDiscard: () => void;
}) {
  const list = cards.length ? cards : [{ id: '', title: fallbackTitle }];
  const jump = (id: string) => { if (id) document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  // v12.10.11.1 (#1477): docked flush to the bottom edge across the CONTENT AREA (.tenant-main - the sidebar is not covered),
  // re-measured when the window or the sidebar changes width. The spacer keeps the last field scrollable clear of the bar.
  const barRef = useRef<HTMLDivElement>(null);
  const [dock, setDock] = useState<{ left: number; width: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const bar = barRef.current;
    const main = (bar?.closest('.tenant-main') as HTMLElement | null) ?? document.querySelector<HTMLElement>('.tenant-main');
    const measure = () => {
      const h = bar?.offsetHeight ?? 0;
      const r = main?.getBoundingClientRect();
      const next = r ? { left: Math.round(r.left), width: main!.clientWidth, h } : { left: 0, width: window.innerWidth, h };
      setDock((d) => (d && d.left === next.left && d.width === next.width && d.h === next.h ? d : next));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) { if (main) ro.observe(main); if (bar) ro.observe(bar); }
    window.addEventListener('resize', measure);
    return () => { ro?.disconnect(); window.removeEventListener('resize', measure); };
  }, []);
  return (
    <>
    <div className="unsaved-bar-spacer" aria-hidden="true" style={dock ? { height: dock.h } : undefined} />
    <div ref={barRef} className="unsaved-bar is-docked" role="region" aria-label="Unsaved changes"
      style={dock ? { left: dock.left, width: dock.width, right: 'auto' } : undefined}>
      <span className="unsaved-bar-dot" aria-hidden="true">●</span>
      <b>{changesText(list.length)}</b>
      <span className="unsaved-bar-names">
        {list.map((c, i) => (
          <span key={c.id || c.title}>{i > 0 && <span aria-hidden="true"> · </span>}
            <button type="button" className="unsaved-bar-name" onClick={() => jump(c.id)}>{c.title}</button></span>
        ))}
      </span>
      <span className="unsaved-bar-actions">
        <button type="button" className="settings-secondary" disabled={saving} onClick={onDiscard}>Discard</button>
        <button type="button" className="settings-primary" disabled={saving} onClick={onSave}>{saving ? 'Saving...' : 'Save now'}</button>
      </span>
    </div>
    </>
  );
}
